import React, { useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import { useMutation, useQuery } from '@apollo/client';
import { useRoute } from '@react-navigation/native';

import { PEOPLE_OPEN_SHIFT_APPLICATION_STATUS } from '../../../common/constants';
import {
	APPLY_FOR_OPEN_SHIFT,
	GET_MY_OPEN_SHIFTS,
	WITHDRAW_MY_OPEN_SHIFT_APPLICATION,
} from '../../../common/graphql/people/roster';
import {
	addDaysToRosterDate,
	compareRosterDates,
	formatRosterDate,
	getTodayDeviceRosterDate,
} from '../../../common/utils/people/roster';
import { useUserContext } from '../../../common/utils/context';
import Text from '../../components/text/Text';
import {
	Card,
	EmptyState,
	PrimaryButton,
	Screen,
	SecondaryButton,
	StatusBadge,
} from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import RosterRow from './components/RosterRow';
import {
	canApplyForOpenShift,
	formatShiftSummary,
	getApplicationBadgeTone,
	ROSTER_DATE_FORMAT,
	UPCOMING_OPEN_SHIFT_DAYS,
} from './rosterHelpers';

// Open shifts in the employee's state for the next four weeks; applying sends the request to
// the roster manager, a pending application can be withdrawn. Opened from a later calendar day
// (route param `date`), it lists the four weeks from that day so the tapped shift is included.
const MyOpenShiftsView = () => {
	const styles = useThemedStyles(createStyles);
	const { isAVC } = useUserContext();
	const { params } = useRoute();
	const today = useMemo(() => getTodayDeviceRosterDate(), []);
	const range = useMemo(() => {
		const defaultToDate = addDaysToRosterDate(today, UPCOMING_OPEN_SHIFT_DAYS);
		const fromDate = params?.date && compareRosterDates(params.date, defaultToDate) > 0 ? params.date : today;
		return { fromDate, toDate: addDaysToRosterDate(fromDate, UPCOMING_OPEN_SHIFT_DAYS) };
	}, [params?.date, today]);
	const isLaterRange = range.fromDate !== today;
	const [busyOpenShiftId, setBusyOpenShiftId] = useState(null);
	const [openShiftError, setOpenShiftError] = useState('');

	const { data, loading, error, refetch } = useQuery(GET_MY_OPEN_SHIFTS, {
		skip: !isAVC,
		variables: range,
		fetchPolicy: 'cache-and-network',
	});
	const [applyForOpenShift] = useMutation(APPLY_FOR_OPEN_SHIFT);
	const [withdrawMyOpenShiftApplication] = useMutation(WITHDRAW_MY_OPEN_SHIFT_APPLICATION);
	const openShifts = data?.getMyOpenShifts || [];

	const applyForShift = useCallback(async (openShift) => {
		setBusyOpenShiftId(openShift.id);
		setOpenShiftError('');
		try {
			await applyForOpenShift({ variables: { id: openShift.id } });
			await refetch();
		}
		catch (mutationError) {
			console.error('Open shift application failed', mutationError);
			setOpenShiftError(mutationError?.message || 'Unable to apply for the shift. Please try again.');
		}
		finally {
			setBusyOpenShiftId(null);
		}
	}, [applyForOpenShift, refetch]);

	const withdrawApplication = useCallback(async (openShift) => {
		setBusyOpenShiftId(openShift.id);
		setOpenShiftError('');
		try {
			await withdrawMyOpenShiftApplication({ variables: { id: openShift.myApplication.id } });
			await refetch();
		}
		catch (mutationError) {
			console.error('Open shift application withdrawal failed', mutationError);
			setOpenShiftError(mutationError?.message || 'Unable to withdraw the application. Please try again.');
		}
		finally {
			setBusyOpenShiftId(null);
		}
	}, [refetch, withdrawMyOpenShiftApplication]);

	return (
		<Screen
			backLabel="My roster"
			heading="Open shifts"
			subheading={isLaterRange
				? `From ${formatRosterDate(range.fromDate, ROSTER_DATE_FORMAT)}. Apply and your roster manager will review.`
				: 'Apply and your roster manager will review.'}
			isLoading={loading && !data}
			error={error && !data ? 'Unable to load open shifts. Please try again.' : null}
			onRetry={() => refetch()}
		>
			{openShiftError ? <Text style={styles.errorText}>{openShiftError}</Text> : null}
			{!openShifts.length ? (
				<Card>
					<EmptyState
						icon="calendar"
						title="No open shifts"
						message="No open shifts available right now."
					/>
				</Card>
			) : (
				<View style={styles.list}>
					{openShifts.map((openShift) => {
						const application = openShift.myApplication;
						const canApply = canApplyForOpenShift(openShift);
						const canWithdraw = Number(application?.status) === PEOPLE_OPEN_SHIFT_APPLICATION_STATUS.PENDING;
						const isBusy = busyOpenShiftId === openShift.id;
						const isOtherBusy = Boolean(busyOpenShiftId) && !isBusy;
						const dateLabel = formatRosterDate(openShift.date, ROSTER_DATE_FORMAT);

						return (
							<RosterRow
								key={openShift.id}
								title={dateLabel}
								details={[
									formatShiftSummary(openShift),
									openShift.notes,
									application?.decisionNotes ? `Decision: ${application.decisionNotes}` : null,
								]}
								right={canApply ? (
									<PrimaryButton
										label="Apply"
										accessibilityLabel={`Apply for ${dateLabel}`}
										onPress={() => applyForShift(openShift)}
										loading={isBusy}
										disabled={isOtherBusy}
										style={styles.applyButton}
									/>
								) : canWithdraw ? (
									<SecondaryButton
										label="Withdraw"
										accessibilityLabel={`Withdraw application for ${dateLabel}`}
										onPress={() => withdrawApplication(openShift)}
										loading={isBusy}
										disabled={isOtherBusy}
									/>
								) : null}
							>
								{application ? (
									<StatusBadge
										label={application.statusLabel}
										tone={getApplicationBadgeTone(application.status)}
										size="small"
										style={styles.badge}
									/>
								) : null}
							</RosterRow>
						);
					})}
				</View>
			)}
		</Screen>
	);
};

const createStyles = ({ ui: { colors, space, touch, type } }) => ({
	list: {
		gap: space.sm,
	},
	badge: {
		marginTop: space.xs,
	},
	applyButton: {
		minHeight: touch.min,
		paddingHorizontal: space.lg,
	},
	errorText: {
		...type.bodySmall,
		color: colors.danger,
	},
});

export default MyOpenShiftsView;
